import type { InfoWithIconGridItem } from '~/components/InfoWithIconGrid';
import type { BreadcrumbItem } from '~/components/ui/breadcrumb';
import type {
	Condo,
	CondoCompanyDistribution,
	CondoExclusiveProperties,
	Condominium,
	CondoPropertiesStatistics,
	CondoPropertiesTypology
} from '~/core/entities/condo/Condo';
import type { CurrencyRange } from '~~/type-utils';

import { validateRange } from '@/utils';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { grammarInflection, isNotEmptyAndHasNoZero } from '~~/utils';

import { RangeLabel } from '~/components/CharacteristicsGrid';

import IconMaterialSymbolsLightApartment from '~icons/material-symbols-light/apartment';
import IconMaterialSymbolsLightBathtubOutline from '~icons/material-symbols-light/bathtub-outline';
import IconMaterialSymbolsLightBedOutlineSharp from '~icons/material-symbols-light/bed-outline-sharp';
import IconMaterialSymbolsLightDirectionsCarOutline from '~icons/material-symbols-light/directions-car-outline';
import IconMaterialSymbolsLightDomain from '~icons/material-symbols-light/domain';
import IconMaterialSymbolsLightElevator from '~icons/material-symbols-light/elevator-outline';
import IconMaterialSymbolsLightHistoryEdu from '~icons/material-symbols-light/history-edu-outline';
import IconMaterialSymbolsLightHomeWork from '~icons/material-symbols-light/home-work-outline';
import IconMaterialSymbolsLightMonetizationOnOutlineRounded from '~icons/material-symbols-light/monetization-on-outline-rounded';
import IconMaterialSymbolsLightOpenInFull from '~icons/material-symbols-light/open-in-full';
import IconMaterialSymbolsLightPriceChangeOutline from '~icons/material-symbols-light/price-change-outline';
import IconMaterialSymbolsLightRequestQuoteOutline from '~icons/material-symbols-light/request-quote-outline';
import IconMaterialSymbolsLightShowerOutline from '~icons/material-symbols-light/shower-outline';
import IconMaterialSymbolsLightSquareFoot from '~icons/material-symbols-light/square-foot';
import IconMaterialSymbolsLightStairs from '~icons/material-symbols-light/stairs-2';
import IconMaterialSymbolsLightStraightenOutline from '~icons/material-symbols-light/straighten-outline';
import IconMaterialSymbolsLightTrolley from '~icons/material-symbols-light/trolley-outline';
import IconMaterialSymbolsLightViewQuilt from '~icons/material-symbols-light/view-quilt-outline';

const isRunningOnClient = import.meta.client;

interface CurrencyPropertyStatistics {
	priceRange: CurrencyRange;
	monthlyCosts: CurrencyMonthlyCosts;
}

interface CurrencyMonthlyCosts {
	condoFee: CurrencyRange;
	tax: CurrencyRange;
}

interface CondoPropertyCharacteristics {
	area: CurrencyRange;
	totalArea: CurrencyRange;
	squareMeterPrice: CurrencyRange;
	bedrooms: string;
	suites: string;
	bathrooms: string;
	parkingSpaces: string;
}

interface DesignDetails {
	title: string;
	value: string;
}

interface UseCondominiumReturn {
	condo: ComputedRef<Condo | null>;
	condoArea: ComputedRef<string>;
	hasCondoPropertyCharacteristics: ComputedRef<boolean>;
	condoPropertyCharacteristics: ComputedRef<CondoPropertyCharacteristics>;
	breadCrumbItems: ComputedRef<BreadcrumbItem[]>;
	hasPropertyStatistics: ComputedRef<boolean>;
	propertyStatistics: ComputedRef<CondoPropertiesStatistics | null>;
	propertyCharacteristics: ComputedRef<InfoWithIconGridItem[]>;
	currencyPropertyStatistics: ComputedRef<CurrencyPropertyStatistics>;
	companyDistribution: ComputedRef<CondoCompanyDistribution | null>;
	exclusiveProperties: ComputedRef<CondoExclusiveProperties>;
	propertiesTypologies: ComputedRef<CondoPropertiesTypology[]>;
	agents: ComputedRef<Condominium['agents']>;
	condoInfoItems: ComputedRef<InfoWithIconGridItem[]>;
	condoValuesAndFees: ComputedRef<InfoWithIconGridItem[]>;
	designDetails: ComputedRef<DesignDetails[]>;
}

/**
 * Composable for managing condominium data and related computations
 * @param condominium - The condominium data (reactive ref)
 */
export function useCondominium(
	condominium: Ref<Condominium | null>
): UseCondominiumReturn {
	const condo = computed<Condo | null>(() => condominium.value?.condo || null);

	const condoArea = computed<string>(() =>
		getFormattedSquareMeter(condominium.value?.condo.area || 0)
	);

	const hasCondoPropertyCharacteristics = computed<boolean>(
		() => !!condominium.value?.condo.propertyCharacteristics
	);

	const condoPropertyCharacteristics = computed<CondoPropertyCharacteristics>(
		() => ({
			area: formatRange(
				condo.value?.propertyCharacteristics?.area,
				getFormattedSquareMeter
			),
			totalArea: formatRange(
				condo.value?.propertyCharacteristics?.totalArea,
				getFormattedSquareMeter
			),
			squareMeterPrice: formatRange(
				condominium.value?.propertyStatistics?.squareMeterPrice,
				getFormattedSquareMeterPrice
			),
			bedrooms: formatNumberArrayWithGrammarInflection(
				condo.value?.propertyCharacteristics?.bedrooms,
				'quarto',
				'quartos'
			),
			suites: formatNumberArrayWithGrammarInflection(
				condo.value?.propertyCharacteristics?.suites,
				'suite',
				'suites'
			),
			bathrooms: formatNumberArrayWithGrammarInflection(
				condo.value?.propertyCharacteristics?.bathrooms,
				'banheiro',
				'banheiros'
			),
			parkingSpaces: formatNumberArrayWithGrammarInflection(
				condo.value?.propertyCharacteristics?.parkingSpaces,
				'vaga',
				'vagas'
			)
		})
	);

	const condoInfoItems = computed<InfoWithIconGridItem[]>(() => {
		const items: InfoWithIconGridItem[] = [];

		if (!condominium.value) return items;

		if (Number(condoArea.value.split('m²')[0])) {
			items.push({
				icon: IconMaterialSymbolsLightSquareFoot,
				label: 'Área construída',
				description: condoArea.value
			});
		}

		if (propertiesTypologies.value?.length) {
			items.push({
				icon: IconMaterialSymbolsLightHomeWork,
				label: 'Tipos de unidade',
				description: `${propertiesTypologies.value?.length}`
			});
		}

		if (condo.value?.launchDate) {
			const description = new Date(condo.value?.launchDate || '')
				.getFullYear()
				.toString();

			items.push({
				icon: IconMaterialSymbolsLightHistoryEdu,
				label: 'Ano de construção',
				description
			});
		}

		if (isNotEmptyAndHasNoZero(condo.value?.unitsPerFloor)) {
			const descriptionValue = formatNumbersArray(condo.value?.unitsPerFloor);
			const description = `${descriptionValue} por andar`;

			items.push({
				icon: IconMaterialSymbolsLightViewQuilt,
				label: 'Apartamentos por andar',
				description
			});
		}

		if (condo.value?.socialElevators) {
			items.push({
				icon: IconMaterialSymbolsLightElevator,
				label: 'Elevadores',
				description: `${condo.value?.socialElevators} sociais`
			});
		}

		if (condo.value?.serviceElevators) {
			items.push({
				icon: IconMaterialSymbolsLightTrolley,
				label: 'Elevadores',
				description: `${condo.value?.serviceElevators} de serviço`
			});
		}

		if (isNotEmptyAndHasNoZero(condo.value?.floors)) {
			const descriptionValue = formatNumbersArray(condo.value?.floors);
			const description = `${descriptionValue} andares`;

			items.push({
				icon: IconMaterialSymbolsLightStairs,
				description
			});
		}

		if (condo.value?.propertyCount) {
			const description = `${condo.value.propertyCount} ${grammarInflection(Number(condo.value.propertyCount), 'unidade', 'unidades')}`;
			items.push({
				icon: IconMaterialSymbolsLightApartment,
				description
			});
		}

		if (condo.value?.towers) {
			const inflection = grammarInflection(
				condo.value?.towers || 0,
				'torre',
				'torres'
			);
			const description = `${condo.value?.towers} ${inflection}`;
			items.push({
				icon: IconMaterialSymbolsLightDomain,
				description
			});
		}

		return items;
	});

	const condoValuesAndFees = computed<InfoWithIconGridItem[]>(() => {
		const costs = [];

		if (validateRange(propertyStatistics.value?.priceRange || null)) {
			costs.push({
				icon: IconMaterialSymbolsLightPriceChangeOutline,
				label: 'Faixa de preço dos imóveis',
				description: h(RangeLabel, {
					rangeStart: currencyPropertyStatistics.value?.priceRange.min,
					rangeEnd: currencyPropertyStatistics.value?.priceRange.max
				})
			});
		}

		if (
			validateRange(propertyStatistics.value?.monthlyCosts.condoFee || null)
		) {
			costs.push({
				icon: IconMaterialSymbolsLightMonetizationOnOutlineRounded,
				label: 'Taxa de condomínio',
				description: h(RangeLabel, {
					rangeStart:
						currencyPropertyStatistics.value?.monthlyCosts.condoFee.min,
					rangeEnd: currencyPropertyStatistics.value?.monthlyCosts.condoFee.max
				})
			});
		}

		if (validateRange(propertyStatistics.value?.monthlyCosts.tax || null)) {
			costs.push({
				icon: IconMaterialSymbolsLightRequestQuoteOutline,
				label: 'IPTU mensal',
				description: h(RangeLabel, {
					rangeStart: currencyPropertyStatistics.value?.monthlyCosts.tax.min,
					rangeEnd: currencyPropertyStatistics.value?.monthlyCosts.tax.max
				})
			});
		}

		return costs;
	});

	const breadCrumbItems = computed<BreadcrumbItem[]>(() => {
		if (!condominium.value) return [];

		return [
			{
				label: 'Início',
				route: '/',
				type: 'home',
				separator: true
			},
			{
				label: `${condo.value?.city} - ${condo.value?.state}`,
				route: PropertyFiltersPresenter.getPathFromFilters({
					cities: [condo.value?.city || ''],
					states: [condo.value?.state || '']
				}),
				type: 'city',
				separator: true
			},
			{
				label: condo.value?.region || '',
				route: PropertyFiltersPresenter.getPathFromFilters({
					cities: [condo.value?.city || ''],
					states: [condo.value?.state || ''],
					regions: [condo.value?.region || '']
				}),
				type: 'region',
				separator: true
			},
			{
				label: condo.value?.name || '',
				route: `/condominio/${condo.value?.slug}`,
				type: 'region',
				separator: false
			}
		];
	});

	const propertyCharacteristics = computed<InfoWithIconGridItem[]>(() => {
		const characteristics: InfoWithIconGridItem[] = [];
		if (!hasCondoPropertyCharacteristics.value) return characteristics;

		const {
			area,
			totalArea,
			squareMeterPrice,
			bedrooms,
			suites,
			bathrooms,
			parkingSpaces
		} = condoPropertyCharacteristics.value;

		if (area.max) {
			const shareMeterPriceDescription = squareMeterPrice.max
				? h(RangeLabel, {
						size: 'xs',
						variant: 'light',
						rangeStart: squareMeterPrice.min,
						rangeEnd: squareMeterPrice.max
					})
				: '';

			characteristics.push({
				label: h(RangeLabel, {
					rangeStart: area.min,
					rangeEnd: area.max,
					title: 'Área útil'
				}),
				description: shareMeterPriceDescription,
				icon: IconMaterialSymbolsLightStraightenOutline
			});
		}

		if (totalArea.max) {
			characteristics.push({
				label: h(RangeLabel, {
					rangeStart: totalArea.min,
					rangeEnd: totalArea.max,
					title: 'Área total'
				}),
				description: '',
				icon: IconMaterialSymbolsLightOpenInFull
			});
		}

		if (bedrooms) {
			characteristics.push({
				label: bedrooms,
				description: '',
				icon: IconMaterialSymbolsLightBedOutlineSharp
			});
		}

		if (suites) {
			characteristics.push({
				label: suites,
				description: '',
				icon: IconMaterialSymbolsLightBathtubOutline
			});
		}

		if (bathrooms) {
			characteristics.push({
				label: bathrooms,
				description: '',
				icon: IconMaterialSymbolsLightShowerOutline
			});
		}

		if (parkingSpaces) {
			characteristics.push({
				label: parkingSpaces,
				description: '',
				icon: IconMaterialSymbolsLightDirectionsCarOutline
			});
		}

		return characteristics;
	});

	const designDetails = computed(() => {
		if (!condominium.value) return [];

		const details = [];

		if (condo.value?.developer) {
			details.push({
				title: 'Incorporação e Construção',
				value: condo.value.developer
			});
		}

		if (condo.value?.architecturalDesign) {
			details.push({
				title: 'Projeto Arquitetônico',
				value: condo.value.architecturalDesign
			});
		}

		if (condo.value?.interiorDesign) {
			details.push({
				title: 'Projeto de Decoração',
				value: condo.value.interiorDesign
			});
		}

		if (condo.value?.landscapeDesign) {
			details.push({
				title: 'Projeto de Paisagismo',
				value: condo.value.landscapeDesign
			});
		}

		return details;
	});

	const hasPropertyStatistics = computed<boolean>(() => {
		const hasPriceRange = validateRange(
			condominium.value?.propertyStatistics?.priceRange || null
		);
		const hasMonthlyCosts = Object.values(
			condominium.value?.propertyStatistics?.monthlyCosts || {}
		).some(Boolean);
		const hasSquareMeterPrice = validateRange(
			condominium.value?.propertyStatistics?.squareMeterPrice || null
		);

		return (
			(hasPriceRange || hasMonthlyCosts || hasSquareMeterPrice) &&
			!!condominium.value?.propertyStatistics?.totalProperties
		);
	});

	const propertyStatistics = computed<CondoPropertiesStatistics | null>(
		() => condominium.value?.propertyStatistics || null
	);

	const currencyPropertyStatistics = computed<CurrencyPropertyStatistics>(
		() => ({
			priceRange: formatRange(
				propertyStatistics.value?.priceRange,
				getFormattedCurrency
			),
			monthlyCosts: {
				condoFee: formatRange(
					propertyStatistics.value?.monthlyCosts.condoFee,
					getFormattedCurrency
				),
				tax: formatRange(
					propertyStatistics.value?.monthlyCosts.tax,
					getFormattedCurrency
				)
			}
		})
	);

	const companyDistribution = computed<CondoCompanyDistribution | null>(
		() => condominium.value?.companyDistribution || null
	);
	const exclusiveProperties = computed<CondoExclusiveProperties>(
		() => condominium.value?.exclusiveProperties || []
	);
	const propertiesTypologies = computed<CondoPropertiesTypology[]>(
		() => condominium.value?.propertiesTypologies || []
	);
	const agents = computed<Condominium['agents']>(
		() => condominium.value?.agents || []
	);

	return {
		condo,
		condoArea,
		hasCondoPropertyCharacteristics,
		condoPropertyCharacteristics,
		condoInfoItems,
		condoValuesAndFees,
		breadCrumbItems,
		hasPropertyStatistics,
		propertyStatistics,
		propertyCharacteristics,
		currencyPropertyStatistics,
		companyDistribution,
		exclusiveProperties,
		propertiesTypologies,
		agents,
		designDetails
	};
}
